import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { ApolloError } from 'apollo-client';
import cleanDeep from 'clean-deep';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { iif, Observable, of, Subscription } from 'rxjs';
import { finalize, map, mergeMap } from 'rxjs/operators';
import {
  ConsentSource,
  FilterType,
  IntakeForm,
  IntakeFormStatus,
  NameFormat,
  QuestionnaireType
} from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { Patient } from 'src/app/core/api/patient.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { FilterService } from 'src/app/filter/filter.service';
import { FormTemplateInputFormGroup } from 'src/app/form-template/form-template-input-form-group';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { nonModifiableAutopopulateConfig } from 'src/app/patients/patient/patient-file/non-modifiable-linking.model';
import { QuestionnairePdfExportService } from 'src/app/questionnaires/questionnaire-pdf-export.service';
import { AutopopulateService } from 'src/app/shared/autopopulate/autopopulate.service';
import { PatientModalComponent } from 'src/app/shared/consult-forms/patient-modal/patient-modal.component';
import { LoadingSpinnerService } from 'src/app/shared/loading-spinner/loading-spinner.service';
import { HealthCardMode } from 'src/app/shared/location-select/location.model';
import { PatientFormGroup } from 'src/app/shared/patient-form/patient-form.model';
import { PersonNamePipe } from 'src/app/shared/shared-pipes/person-name.pipe';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { IntakeFormPdfExportService } from '../intake-form-pdf-export.service';
import {
  allIntakeFormSchemas,
  generalButtonMap,
  IntakeFormGroupType,
  IntakeFormSchema,
  IntakeFormType,
  standardIntakeFormSchema
} from '../intake-form-schema';
import { IntakeFormService } from '../intake-form.service';
import { LinkWithSimilarPatientModalComponent } from '../link-with-similar-patient-modal/link-with-similar-patient-modal.component';
import { PatientService } from './../../core/api/patient.service';
import { PatientFileService } from './../../patients/patient/patient-file/patient-file.service';

export interface SearchablePatient {
  patient: Partial<Patient>;
  score: number;
}
const patientKeyWeight: { [key: string]: any } = {
  firstName: 4,
  lastName: 4,
  address: 2,
  gender: 0,
  email: 4,
  phone: 4,
  healthCard: {
    province: 1,
    number: 8
  }
};

@Component({
  selector: 'csi-intake-form-view',
  templateUrl: './intake-form-view.component.html',
  styleUrls: ['./intake-form-view.component.scss'],
  providers: [PersonNamePipe, QuestionnaireScorePipe]
})
export class IntakeFormViewComponent implements OnInit, OnDestroy {
  constructor(
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: IntakeForm,
    private intakeFormService: IntakeFormService,
    private clinicSetupService: ClinicSetupService,
    private staffService: StaffService,
    private intakeFormPdfService: IntakeFormPdfExportService,
    private loadingSpinnerService: LoadingSpinnerService,
    private errorHandlerService: ErrorHandlerService,
    private filterService: FilterService,
    private patientService: PatientService,
    private router: Router,
    private dialog: MatDialog,
    private patientFileService: PatientFileService,
    private autopopulateService: AutopopulateService,
    private questionnairePdfExportService: QuestionnairePdfExportService,
    private questionnaireScorePipe: QuestionnaireScorePipe,
    private appStateService: AppStateService
  ) {}
  public isLoaded: boolean;
  public buttonMap = generalButtonMap;
  public intakeFormGroup = new FormGroup({});
  public intakeFormSubscription: Subscription;

  public intakeForm: IntakeForm;
  public loading: boolean;

  public isLinked: boolean;

  public patientFormGroup = new PatientFormGroup();
  public patient: Patient;
  public readonly NameFormat = NameFormat;
  public readonly HealthCardMode = HealthCardMode;
  public healthCardConfig: HealthCardMode;
  public questionnaire;

  public intakeFormGroups = {};

  public intakeFormSchema: IntakeFormSchema;
  public standardIntakeFormSchema = standardIntakeFormSchema;
  public IntakeFormGroupType = IntakeFormGroupType;
  public intakeFormResponse = {};

  public intakeFormChangeTime: any;

  public schema;
  public formTemplateInputFormGroup: FormTemplateInputFormGroup;
  public downloadingPDF = false;

  ngOnInit() {
    if (this.data) {
      this.intakeForm = this.data;
      this.intakeFormResponse = safeJsonParse(this.intakeForm.response);
      this.loadIntakeFormResponse(this.intakeFormResponse, this.intakeForm['type']);
      this.isLinked = !!this.intakeForm.intakeFormPatientId;
      this.isLoaded = true;
    }

    this.appStateService.currentIntakeForm$
      .pipe(untilDestroyed(this))
      .subscribe(currentIntakeForm => {
        if (this.data) {
          // Intake form modal uses this.data so do not overwrite
          return;
        }
        this.intakeForm = currentIntakeForm;
        if (!this.intakeForm) {
          return;
        }
        this.isLinked = !!this.intakeForm.intakeFormPatientId;
        this.intakeFormResponse = safeJsonParse(currentIntakeForm.response);
        this.loadIntakeFormResponse(this.intakeFormResponse, currentIntakeForm['type']);

        this.isLoaded = true;
      });
  }

  loadIntakeFormResponse(intakeFormResponse: any, intakeFormType: string) {
    if (this.intakeForm.config) {
      const config = JSON.parse(this.intakeForm.config);
      this.schema = config['schema'];
      this.formTemplateInputFormGroup = new FormTemplateInputFormGroup(this.schema);
      this.formTemplateInputFormGroup.patchValue(intakeFormResponse.answers);
      this.formTemplateInputFormGroup.disable();
    }

    this.questionnaire = intakeFormResponse.questionnaire || this.intakeForm;

    if (intakeFormResponse.answers && intakeFormResponse.answers.patientInformation) {
      // New intake form
      this.patient = intakeFormResponse['answers']['patientInformation'];
    } else {
      // DEPRECATED intake form
      this.intakeFormSchema =
        allIntakeFormSchemas[intakeFormType] || allIntakeFormSchemas[IntakeFormType.STANDARD];
      this.buildIntakeFormGroup();
      this.intakeFormService.transformDeprecatedResponseKeys(
        intakeFormResponse,
        this.intakeFormSchema
      );

      this.intakeFormGroup.patchValue(intakeFormResponse);
      this.patient = intakeFormResponse.patientInformation;
    }
  }

  buildIntakeFormGroup() {
    if (this.intakeFormSubscription) {
      this.intakeFormSubscription.unsubscribe();
    }

    this.intakeFormGroup = new FormGroup({});
    Object.keys(this.intakeFormSchema.groups).forEach(key => {
      const group = this.intakeFormSchema.groups[key];
      if (group.type === IntakeFormGroupType.PatientCard) {
        const pGroup = new PatientFormGroup();
        pGroup.setValidators(this.patientService.contactValidator);
        this.intakeFormGroup.addControl(key, pGroup);
      } else {
        this.intakeFormGroup.addControl(key, this.generateFormGroup(group.config));
      }
    });

    this.intakeFormSubscription = this.intakeFormGroup.valueChanges.subscribe(() => {
      this.intakeFormChangeTime = new Date().getTime();
    });
  }

  generateFormGroup(questionList: any) {
    const formGroup = new FormGroup({});
    Object.keys(questionList).forEach(question => {
      const validators = questionList[question].validators || null;
      const questions = questionList[question];
      if (questions.inputType.type === 'checkbox') {
        formGroup.addControl(question, new FormControl(false, validators));
      } else if (questions.inputType.type === 'checkboxGroup') {
        formGroup.addControl(
          question,
          new FormGroup(
            Object.keys(questions.inputType.checkboxes).reduce(
              (group, key) => ({ ...group, [key]: new FormControl() }),
              {}
            ),
            validators
          )
        );
      } else {
        formGroup.addControl(question, new FormControl(null, validators));
      }
    });
    return formGroup;
  }

  ngOnDestroy(): void {
    if (this.intakeFormSubscription) {
      this.intakeFormSubscription.unsubscribe();
    }
  }

  noSort() {
    return 0;
  }

  generatePDF() {
    this.loadingSpinnerService.show();
    this.downloadingPDF = true;
    if (this.intakeForm.config) {
      const questionnaireKey = this.schema.abbreviation;
      this.questionnairePdfExportService
        .downloadQuestionnaireAsPDF(
          questionnaireKey,
          {
            ...this.intakeForm,
            schema: this.schema,
            formGroupWithValues: this.formTemplateInputFormGroup,
            totalScore: this.questionnaireScorePipe.transform(this.intakeForm)
          },
          this.intakeForm.intakeFormPatientId,
          false
        )
        .pipe(
          finalize(() => {
            this.loadingSpinnerService.hide();
            this.downloadingPDF = false;
          })
        )
        .subscribe({
          error: (error: ApolloError) => {
            this.errorHandlerService.handleGraphQLError(error, true);
          }
        });
    } else {
      this.intakeFormPdfService
        .downloadPDFForIntakeForm(this.intakeFormResponse, this.clinicSetupService.clinic, false)
        .pipe(
          finalize(() => {
            this.loadingSpinnerService.hide();
            this.downloadingPDF = false;
          })
        )
        .subscribe({
          error: (error: ApolloError) => {
            this.errorHandlerService.handleGraphQLError(error, true);
          }
        });
    }
  }

  checkForSimilarPatients() {
    this.loadingSpinnerService.show();
    this.getMatchingPatients(this.patient)
      .pipe(
        mergeMap(result => {
          this.loadingSpinnerService.hide();
          if (result && result.length >= 1) {
            return LinkWithSimilarPatientModalComponent.openLinkWithSimilarPatientModel(
              this.dialog,
              result,
              this.patient
            ).afterClosed();
          }
          return of('new');
        }),
        mergeMap((patientInformation: Patient | 'new') => {
          if (!patientInformation) {
            return of(null);
          }
          if (patientInformation === 'new') {
            return PatientModalComponent.open(
              this.dialog,
              this.staffService.staff,
              this.patient,
              null,
              null,
              true
            ).afterClosed();
          } else {
            return PatientModalComponent.open(
              this.dialog,
              this.staffService.staff,
              { ...patientInformation, ...this.patient, id: patientInformation.id },
              null,
              null,
              true
            ).afterClosed();
          }
        }),
        mergeMap((linkedPatientInformation: Patient) =>
          this.intakeFormService.updatePatientWithIntakeFormNonModifiableData(
            linkedPatientInformation,
            this.intakeForm
          )
        ),
        mergeMap((linkedPatientInformation: Patient) =>
          this.updateIntakeForm(linkedPatientInformation)
        ),
        mergeMap((intakeForm: IntakeForm) => this.updatePatientWithQuestionnaire(intakeForm))
      )
      .subscribe(intakeForm => {
        if (intakeForm) {
          this.isLinked = true;
          this.intakeForm = intakeForm;
        }
        this.loadingSpinnerService.hide();
      });
  }

  updateIntakeForm(patientInformation: Patient) {
    if (patientInformation) {
      this.loadingSpinnerService.show();

      return this.intakeFormService.updateIntakeForm({
        id: this.intakeForm.id,
        intakeFormPatientId: patientInformation.id,
        status: IntakeFormStatus.processed,
        statusId: this.intakeFormService.generateIntakeFormStatusId(
          this.clinicSetupService.clinic.id,
          IntakeFormStatus.processed
        )
      });
    }
    return of(null);
  }

  updatePatientWithQuestionnaire(intakeForm: IntakeForm) {
    if (intakeForm) {
      if (this.questionnaire.response) {
        return this.patientService
          .createLinkedPatientIntakeQuestionnaire(this.getQuestionnaireFormGroup())
          .pipe(map(() => intakeForm));
      }
      this.patientService.updatePatient({
        id: intakeForm.intakeFormPatientId
      });
    }
    return of(null);
  }

  updatePatientWithNonModifiableData(linkedPatientInformation: Patient) {
    if (!linkedPatientInformation) {
      return of(null);
    }

    let formGroup = this.patientFileService.buildFormGroup();
    if (linkedPatientInformation.nonModifiableData) {
      formGroup.patchValue(
        typeof linkedPatientInformation.nonModifiableData === 'string'
          ? JSON.parse(linkedPatientInformation.nonModifiableData)
          : linkedPatientInformation.nonModifiableData
      );
    }
    this.autopopulateService.populateFormGroup(
      formGroup,
      nonModifiableAutopopulateConfig,
      this.intakeForm.response['answers']
    );
    return this.patientService.updatePatient({
      id: linkedPatientInformation.id,
      nonModifiableData: JSON.stringify(formGroup.value)
    });
  }

  public openLinkedPatient() {
    this.patientService.getPatient(this.intakeForm.intakeFormPatientId).subscribe(patient => {
      if (patient) {
        PatientModalComponent.open(
          this.dialog,
          this.staffService.staff,
          patient,
          null,
          null,
          null,
          true
        );
      }
    });
  }

  public openPatientProfile() {
    this.router.navigateByUrl(`/patients/${this.intakeForm.intakeFormPatientId}`);
  }

  private getMatchingPatients(patient: Patient): Observable<Partial<SearchablePatient>[]> {
    this.loading = true;
    return iif(
      () => !!this.staffService.staff && !!this.clinicSetupService.clinic,
      this.filterService.queryFilterData(FilterType.patient, null),
      of(null)
    ).pipe(
      map((result: Partial<Patient[]>) => {
        return this.filterMatchList(patient, result);
      })
    );
  }
  private filterMatchList(patient: Patient, results?: Patient[]): Partial<SearchablePatient>[] {
    const cleanedPatientData = cleanDeep(patient);
    const cleanedPatientList = cleanDeep(results);
    return this.similarPatients(cleanedPatientData, cleanedPatientList);
  }

  private similarPatients(
    cleanedPatientData: Partial<Patient>,
    cleanedPatientList: Patient[]
  ): Partial<SearchablePatient>[] {
    const similarityThreshold = 8;
    return cleanedPatientList
      .map(patient => {
        const score = this.getSortWeight(cleanedPatientData, patient);
        return { patient, score };
      })
      .filter(patient => {
        if (patient.score >= similarityThreshold) {
          return patient;
        }
      })
      .sort((a, b) => b.score - a.score);
  }

  public getSortWeight(
    patientData: Partial<Patient>,
    patientToCompare: Patient,
    weight: any = patientKeyWeight
  ): number {
    let score = 0;
    for (const key in weight) {
      if (patientToCompare.hasOwnProperty(key) && patientData.hasOwnProperty(key)) {
        if (
          typeof patientData[key] === 'object' &&
          typeof patientToCompare[key] === 'object' &&
          typeof weight[key] === 'object'
        ) {
          score += this.getSortWeight(patientData[key], patientToCompare[key], weight[key]);
        } else if (
          this.areEqual(patientData[key as keyof Patient], patientToCompare[key as keyof Patient])
        ) {
          score += weight[key];
        }
      }
    }
    return score;
  }

  public static openIntakeFormDialog(matDialogService: MatDialog, patientIntakeForm: IntakeForm) {
    return matDialogService.open(IntakeFormViewComponent, {
      width: '55rem',
      maxHeight: '90dvh',
      data: patientIntakeForm
    });
  }

  public closeDialogBox() {
    this.dialog.closeAll();
  }

  private areEqual(value1: any, value2: any): boolean {
    if (typeof value1 === 'string' && typeof value2 === 'string') {
      return value1.trim().toLowerCase() === value2.trim().toLowerCase();
    }
    return value1 === value2;
  }

  private getQuestionnaireFormGroup() {
    let questionnaireResponse = {};
    if (this.questionnaire.response) {
      questionnaireResponse = {
        answers: safeJsonParse(this.questionnaire.response).answers,
        scores: safeJsonParse(this.questionnaire.response).scores,

        consent: {
          consentsToPrivacyForm: true,
          consentsToResearch: true,
          agreedToTerms: true,
          consentSource: ConsentSource.Patient
        }
      };
      return new FormBuilder().group({
        type: QuestionnaireType.CUSTOM,
        response: JSON.stringify(questionnaireResponse),
        questionnaireRequestPatientId: this.intakeForm.intakeFormPatientId,
        questionnaireRequestClinicId: this.intakeForm.intakeFormClinicId,
        country: (this.patient.healthCard && this.patient.healthCard.country) || 'US',
        completedAt: this.intakeForm.createdAt,
        config: this.questionnaire.config
      }).value;
    }
  }
}
